import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Loader2, Plus } from 'lucide-react';

const SearchModal = ({ isOpen, onClose, menuItems = [], onAddToOrder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const modalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      inputRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (searchTerm) {
      setIsSearching(true);
      const timeoutId = setTimeout(() => {
        const filtered = menuItems.filter(item => 
          item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredItems(filtered);
        setIsSearching(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setFilteredItems([]);
      setIsSearching(false);
    }
  }, [searchTerm, menuItems]);

  const handleAddToOrder = (item) => {
    onAddToOrder(item);
    // Optional: You can add a confirmation toast or feedback here
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 transition-opacity duration-200 bg-black/60 backdrop-blur-sm">
      <div 
        ref={modalRef}
        className="w-full max-w-2xl mx-4 overflow-hidden transition-all duration-200 transform bg-white shadow-2xl rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-amber-100">
          <h2 className="text-xl font-semibold text-amber-900">Search Menu</h2>
          <button 
            onClick={onClose}
            className="p-2 transition-colors rounded-full text-amber-500 hover:bg-amber-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by dish name or description..."
              className="w-full p-3 pl-10 transition-all duration-200 border-2 bg-amber-50/50 border-amber-200 rounded-xl focus:border-amber-500 focus:outline-none focus:ring-0"
              autoFocus
            />
            <Search className="absolute w-5 h-5 transform -translate-y-1/2 text-amber-400 left-3 top-1/2" />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute p-1 transition-colors transform -translate-y-1/2 rounded-full right-3 top-1/2 hover:bg-amber-100"
              >
                <X className="w-4 h-4 text-amber-500" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {isSearching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="p-2">
              {filteredItems.map((item) => (
                <div 
                  key={item._id} 
                  className="flex items-center p-3 transition-colors rounded-xl hover:bg-amber-50 group"
                >
                  <div className="relative w-16 h-16 overflow-hidden rounded-lg">
                    <img 
                      src={item.imageUrl} 
                      alt={item.label} 
                      className="object-cover w-full h-full"
                    />
                    <button
                      onClick={() => handleAddToOrder(item)}
                      className="absolute p-1.5 bg-amber-500 rounded-full shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 right-1 bottom-1 hover:bg-amber-600"
                    >
                      <Plus className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0 ml-4">
                    <h3 className="font-medium text-amber-900">{item.label}</h3>
                    <p className="mt-1 text-sm text-amber-600 line-clamp-1">
                      {item.description}
                    </p>
                    <p className="mt-1 text-sm font-medium text-amber-700">
                      ${item.price.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : searchTerm ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-amber-100">
                <Search className="w-8 h-8 text-amber-500" />
              </div>
              <p className="text-lg font-medium text-amber-900">No items found</p>
              <p className="mt-2 text-sm text-amber-600">
                Try searching for something else
              </p>
            </div>
          ) : null}
        </div>

        {/* Quick Suggestions */}
        {!searchTerm && (
          <div className="p-4 border-t border-amber-100">
            <h3 className="mb-3 text-sm font-medium text-amber-700">Popular Searches</h3>
            <div className="flex flex-wrap gap-2">
              {['Pizza', 'Cheese', 'Bread', 'Chicken'].map((term) => (
                <button
                  key={term}
                  onClick={() => setSearchTerm(term)}
                  className="px-3 py-1.5 text-sm text-amber-700 bg-amber-50 rounded-full hover:bg-amber-100 transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;